/**
 * Weekly Grid Component
 * Displays menus for a week with RSVP size selection and feedback controls
 */

import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SizeSelector, type RSVPSize } from './SizeSelector';
import { FeedbackInput } from './FeedbackInput';
import { MenuRatingStars } from './MenuRatingStars';
import { OptionalRecipeToggles } from './OptionalRecipeToggles';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDate, isSunday, isPastDate, isToday, toISODate, fromISODate } from '@/lib/utils/dateHelpers';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { hasActiveRSVPCutoffOverride, isMenuPastForMemberRSVP } from '@/lib/utils/menuHelpers';
import type { MenuRatingSummary, Menu } from '@/types/api';
import { RSVP_SIZES, type RSVPSizeCode } from '@/types/units';

export interface RSVPSelection {
  menuId: number;
  size: RSVPSize;
}

interface WeeklyGridProps {
  weekDates: Date[];
  menus: Map<string, Menu>;
  selections: Map<number, RSVPSize>;
  feedback: Map<number, string>;
  ratings?: Map<number, MenuRatingSummary>;
  ratingsDisabled?: boolean;
  pendingRatingMenuIds?: ReadonlySet<number>;
  optOuts: Map<number, Set<number>>;
  cutoffByMenuId?: Map<number, boolean>;
  highlightedDate?: string | null;
  onSelectionChange: (menuId: number, size: RSVPSize) => void;
  onFeedbackChange: (menuId: number, text: string) => void;
  onRatingChange?: (menuId: number, rating: number) => void;
  onOptOutChange: (menuId: number, menuRecipeId: number, included: boolean) => void;
  onApplyToAll: (size: RSVPSizeCode) => void;
  onSubmit?: () => void;
  hasChanges?: boolean;
  submitting?: boolean;
  disabled?: boolean;
}

/**
 * WeeklyGrid - Displays week's menus with RSVP and feedback controls
 *
 * Features:
 * - Desktop: Table layout with Day, Menu, Size, Feedback columns
 * - Mobile: Card layout with stacked fields
 * - Sunday: Always disabled (no FMB)
 * - Announcements: RSVP and Feedback disabled
 * - Past dates: RSVP disabled, Feedback enabled
 */
export function WeeklyGrid({
  weekDates,
  menus,
  selections,
  feedback,
  ratings = emptyRatingMap,
  ratingsDisabled = false,
  pendingRatingMenuIds = emptySet,
  optOuts,
  cutoffByMenuId,
  highlightedDate = null,
  onSelectionChange,
  onFeedbackChange,
  onRatingChange = () => {},
  onOptOutChange,
  onApplyToAll,
  onSubmit,
  hasChanges,
  submitting,
  disabled = false,
}: WeeklyGridProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const saveControlsRef = useRef<HTMLDivElement | null>(null);
  const [topSaveControlsVisible, setTopSaveControlsVisible] = useState(true);

  useEffect(() => {
    if (isDesktop || !onSubmit) {
      setTopSaveControlsVisible(true);
      return;
    }

    const target = saveControlsRef.current;
    if (!target || !('IntersectionObserver' in window)) {
      setTopSaveControlsVisible(false);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setTopSaveControlsVisible(entry.isIntersecting);
    }, { threshold: 0.01 });

    observer.observe(target);
    return () => observer.disconnect();
  }, [isDesktop, onSubmit]);

  const showMobileFloatingSave = Boolean(
    onSubmit && hasChanges && !isDesktop && !topSaveControlsVisible
  );

  return (
    <div className={cn('space-y-4', showMobileFloatingSave && 'pb-24 md:pb-0')}>
      {/* Apply to all + Save controls */}
      <div
        ref={saveControlsRef}
        className="flex flex-col gap-2 border-b pb-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="w-full text-sm text-muted-foreground min-[360px]:w-auto">
            Apply to all:
          </span>
          {RSVP_SIZES.map((size) => (
            <Button
              key={size}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onApplyToAll(size)}
              disabled={disabled}
            >
              All {size}
            </Button>
          ))}
        </div>
        {onSubmit && (
          <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
            {hasChanges && (
              <span className="text-sm text-amber-600">Unsaved changes</span>
            )}
            <Button
              onClick={onSubmit}
              disabled={submitting || !hasChanges}
              size="sm"
              className="shrink-0"
            >
              {submitting ? 'Saving...' : 'Save All'}
            </Button>
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4 font-medium text-muted-foreground w-36">
                Day
              </th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                Menu
              </th>
              <th className="text-center py-3 px-4 font-medium text-muted-foreground w-32">
                Size
              </th>
              <th className="text-left py-3 px-4 font-medium text-muted-foreground w-64">
                Feedback
              </th>
            </tr>
          </thead>
          <tbody>
            {weekDates.map((date) => {
              const menu = menus.get(toISODate(date));
              const isHighlighted = toISODate(date) === highlightedDate;
              return (
                <WeeklyGridRow
                  key={toISODate(date)}
                  date={date}
                  menu={menu}
                  selection={menu ? selections.get(menu.id) ?? null : null}
                  feedbackText={menu ? feedback.get(menu.id) ?? '' : ''}
                  ratingSummary={menu ? ratings.get(menu.id) ?? emptyRatingSummary : emptyRatingSummary}
                  ratingPending={ratingsDisabled || (menu ? pendingRatingMenuIds.has(menu.id) : false)}
                  optedOutIds={menu ? optOuts.get(menu.id) ?? emptySet : emptySet}
                  cutoffPassed={menu ? cutoffByMenuId?.get(menu.id) ?? false : false}
                  highlighted={isDesktop && isHighlighted}
                  onSelectionChange={onSelectionChange}
                  onFeedbackChange={onFeedbackChange}
                  onRatingChange={onRatingChange}
                  onOptOutChange={onOptOutChange}
                  disabled={disabled}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {weekDates.map((date) => {
          const menu = menus.get(toISODate(date));
          const isHighlighted = toISODate(date) === highlightedDate;
          return (
            <WeeklyGridCard
              key={toISODate(date)}
              date={date}
              menu={menu}
              selection={menu ? selections.get(menu.id) ?? null : null}
              feedbackText={menu ? feedback.get(menu.id) ?? '' : ''}
              ratingSummary={menu ? ratings.get(menu.id) ?? emptyRatingSummary : emptyRatingSummary}
              ratingPending={ratingsDisabled || (menu ? pendingRatingMenuIds.has(menu.id) : false)}
              optedOutIds={menu ? optOuts.get(menu.id) ?? emptySet : emptySet}
              cutoffPassed={menu ? cutoffByMenuId?.get(menu.id) ?? false : false}
              highlighted={!isDesktop && isHighlighted}
              onSelectionChange={onSelectionChange}
              onFeedbackChange={onFeedbackChange}
              onRatingChange={onRatingChange}
              onOptOutChange={onOptOutChange}
              disabled={disabled}
            />
          );
        })}
      </div>

      {showMobileFloatingSave && onSubmit && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 border-t bg-background/95 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_rgb(0_0_0_/_0.12)] backdrop-blur md:hidden"
          data-testid="mobile-save-all-bar"
        >
          <div className="mx-auto flex max-w-4xl items-center gap-3">
            <span className="min-w-0 flex-1 text-sm font-medium text-amber-600">
              Unsaved changes
            </span>
            <Button
              onClick={onSubmit}
              disabled={submitting || !hasChanges}
              className="shrink-0 px-5"
            >
              {submitting ? 'Saving...' : 'Save All'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Internal Components
// ============================================================================

const emptySet = new Set<number>();
const emptyRatingSummary: MenuRatingSummary = { averageRating: null, ratingCount: 0, currentUserRating: null };
const emptyRatingMap = new Map<number, MenuRatingSummary>();

function useHighlightedScroll<T extends HTMLElement>(highlighted: boolean) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!highlighted || !ref.current) return;
    ref.current.scrollIntoView?.({
      block: 'center',
      behavior: 'smooth',
    });
  }, [highlighted]);

  return ref;
}

interface RowCardProps {
  date: Date;
  menu: Menu | undefined;
  selection: RSVPSize;
  feedbackText: string;
  ratingSummary: MenuRatingSummary;
  ratingPending: boolean;
  optedOutIds: Set<number>;
  cutoffPassed?: boolean;
  highlighted?: boolean;
  onSelectionChange: (menuId: number, size: RSVPSize) => void;
  onFeedbackChange: (menuId: number, text: string) => void;
  onRatingChange: (menuId: number, rating: number) => void;
  onOptOutChange: (menuId: number, menuRecipeId: number, included: boolean) => void;
  disabled?: boolean;
}

/**
 * Compute display state for a menu day
 */
function useMenuDayState(
  date: Date,
  menu: Menu | undefined,
  disabled: boolean,
  cutoffPassed: boolean,
  selection: RSVPSize | null
) {
  const sunday = isSunday(date);
  const today = isToday(date);
  const isAnnouncement = menu?.type === 'ANNOUNCEMENT';
  const hasMenu = menu && menu.status === 'PUBLISHED' && !isAnnouncement;
  const hasActiveOverride = !!(hasMenu && hasActiveRSVPCutoffOverride(menu, cutoffPassed));
  const past = menu ? isMenuPastForMemberRSVP(menu, cutoffPassed) : isPastDate(date);
  const isRSVPDisabled = disabled || sunday || past || !hasMenu || cutoffPassed;
  const isFeedbackDisabled = sunday || isAnnouncement || !menu;
  const hasOptionalRecipes = hasMenu && menu.recipes.some((r) => r.isOptional);
  const isActiveSize = selection !== null && selection !== 'SKIP';
  const showToggleEditable = hasMenu && isActiveSize && !isRSVPDisabled && hasOptionalRecipes;
  const showToggleReadOnly = hasMenu && isActiveSize && isRSVPDisabled && hasOptionalRecipes;

  return {
    sunday,
    past,
    today,
    isAnnouncement,
    hasMenu,
    isRSVPDisabled,
    isFeedbackDisabled,
    hasActiveOverride,
    showToggleEditable,
    showToggleReadOnly,
  };
}

/**
 * Renders the optional recipe toggles (editable or read-only) for a menu day.
 * Shared between WeeklyGridRow (desktop) and WeeklyGridCard (mobile).
 */
function OptionalRecipeSection({
  menu,
  optedOutIds,
  showEditable,
  showReadOnly,
  onOptOutChange,
}: {
  menu: Menu;
  optedOutIds: Set<number>;
  showEditable: boolean;
  showReadOnly: boolean;
  onOptOutChange: (menuId: number, menuRecipeId: number, included: boolean) => void;
}) {
  if (!showEditable && !showReadOnly) return null;
  return (
    <OptionalRecipeToggles
      recipes={menu.recipes}
      optedOutIds={optedOutIds}
      onChange={showEditable
        ? (menuRecipeId, included) => onOptOutChange(menu.id, menuRecipeId, included)
        : () => {}}
      readOnly={showReadOnly}
    />
  );
}

/**
 * Renders the menu title cell content for both desktop row and mobile card views.
 * Handles Sunday ("No FMB"), announcements, regular menus, and the no-menu fallback.
 * `variant` controls the title size and description clamping that differs between layouts.
 */
function MenuTitleCell({
  sunday,
  isAnnouncement,
  menu,
  variant,
}: {
  sunday: boolean;
  isAnnouncement: boolean;
  menu: Menu | undefined;
  variant: 'row' | 'card';
}) {
  if (sunday) {
    return <span className="text-destructive italic">No FMB</span>;
  }
  if (isAnnouncement && menu) {
    return <span className="text-destructive italic">{menu.title}</span>;
  }
  if (!menu) {
    return (
      <span className="text-muted-foreground italic">
        {variant === 'row' ? 'No menu' : 'No menu available'}
      </span>
    );
  }
  return (
    <div>
      <span className={cn('font-medium', variant === 'card' && 'text-lg')}>{menu.title}</span>
      {menu.cookingDate && (
        <p className="text-xs text-muted-foreground">
          Cooking day: {formatDate(fromISODate(menu.cookingDate), 'EEE, MMM d')}
        </p>
      )}
      {menu.description && (
        <p
          className={cn(
            'text-sm text-muted-foreground',
            variant === 'row' ? 'truncate max-w-xs' : 'line-clamp-2'
          )}
        >
          {menu.description}
        </p>
      )}
    </div>
  );
}

function LiveOverrideIndicator() {
  return (
    <span
      aria-label="Custom RSVP cutoff override is live"
      className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-green-500" aria-hidden="true" />
      Live
    </span>
  );
}

/**
 * Table row for desktop view
 */
function WeeklyGridRow({
  date,
  menu,
  selection,
  feedbackText,
  ratingSummary,
  ratingPending,
  optedOutIds,
  cutoffPassed = false,
  highlighted = false,
  onSelectionChange,
  onFeedbackChange,
  onRatingChange,
  onOptOutChange,
  disabled = false,
}: RowCardProps) {
  const { sunday, past, today, isAnnouncement, hasMenu, isRSVPDisabled, isFeedbackDisabled, hasActiveOverride, showToggleEditable, showToggleReadOnly } =
    useMenuDayState(date, menu, disabled, cutoffPassed, selection);
  const highlightedRef = useHighlightedScroll<HTMLTableRowElement>(highlighted);
  const highlightedCellClass = highlighted
    ? 'bg-amber-50/80 border-y-2 border-amber-300 first:border-l-2 last:border-r-2'
    : '';

  return (
    <tr
      ref={highlightedRef}
      data-highlighted={highlighted ? 'true' : undefined}
      className={cn(
        'border-b transition-colors',
        today && !highlighted && 'bg-primary/5',
        sunday && 'bg-muted/50',
        past && 'opacity-80'
      )}
    >
      {/* Date Column */}
      <td className={cn('py-4 px-4 align-top', highlightedCellClass)}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('font-medium', today && 'text-primary')}>
            {formatDate(date, 'EEE, MMM d')}
          </span>
          {today && (
            <Badge variant="outline" className="text-xs">
              Today
            </Badge>
          )}
          {hasActiveOverride && <LiveOverrideIndicator />}
        </div>
      </td>

      {/* Menu Column */}
      <td className={cn('py-4 px-4 align-top', highlightedCellClass)}>
        <MenuTitleCell sunday={sunday} isAnnouncement={isAnnouncement} menu={menu} variant="row" />
      </td>

      {/* Size Selection Column */}
      <td className={cn('py-4 px-4 align-top text-center', highlightedCellClass)}>
        {hasMenu ? (
          <div>
            <SizeSelector
              value={selection}
              onChange={(size) => onSelectionChange(menu.id, size)}
              disabled={isRSVPDisabled}
              className="mx-auto"
            />
            <OptionalRecipeSection
              menu={menu}
              optedOutIds={optedOutIds}
              showEditable={showToggleEditable}
              showReadOnly={showToggleReadOnly}
              onOptOutChange={onOptOutChange}
            />
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </td>

      {/* Feedback Column */}
      <td className={cn('py-4 px-4 align-top', highlightedCellClass)}>
        {!sunday && menu ? (
          <>
            <FeedbackInput
              value={feedbackText}
              onChange={(text) => onFeedbackChange(menu.id, text)}
              disabled={isFeedbackDisabled}
            />
            <MenuRatingStars summary={ratingSummary} disabled={isFeedbackDisabled || ratingPending} onRate={(rating) => onRatingChange(menu.id, rating)} />
          </>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

/**
 * Card for mobile view
 */
function WeeklyGridCard({
  date,
  menu,
  selection,
  feedbackText,
  ratingSummary,
  ratingPending,
  optedOutIds,
  cutoffPassed = false,
  highlighted = false,
  onSelectionChange,
  onFeedbackChange,
  onRatingChange,
  onOptOutChange,
  disabled = false,
}: RowCardProps) {
  const { sunday, past, today, isAnnouncement, hasMenu, isRSVPDisabled, isFeedbackDisabled, hasActiveOverride, showToggleEditable, showToggleReadOnly } =
    useMenuDayState(date, menu, disabled, cutoffPassed, selection);
  const highlightedRef = useHighlightedScroll<HTMLDivElement>(highlighted);

  return (
    <Card
      ref={highlightedRef}
      data-highlighted={highlighted ? 'true' : undefined}
      className={cn(
        'gap-0 p-4',
        highlighted && 'border-2 border-amber-300 bg-amber-50/80 shadow-sm',
        today && !highlighted && 'border-primary bg-primary/5',
        sunday && 'bg-muted/50',
        past && 'opacity-80'
      )}
    >
      {/* Header: Date */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className={cn('font-medium', today && 'text-primary')}>
          {formatDate(date, 'EEEE, MMM d')}
        </span>
        {today && (
          <Badge variant="outline" className="text-xs">
            Today
          </Badge>
        )}
        {hasActiveOverride && <LiveOverrideIndicator />}
      </div>

      {/* Menu Info */}
      <div className="mb-2">
        <MenuTitleCell sunday={sunday} isAnnouncement={isAnnouncement} menu={menu} variant="card" />
      </div>

      {/* Size Selection */}
      {hasMenu && (
        <div className="mb-2">
          <SizeSelector
            value={selection}
            onChange={(size) => onSelectionChange(menu.id, size)}
            disabled={isRSVPDisabled}
          />
          <OptionalRecipeSection
            menu={menu}
            optedOutIds={optedOutIds}
            showEditable={showToggleEditable}
            showReadOnly={showToggleReadOnly}
            onOptOutChange={onOptOutChange}
          />
        </div>
      )}

      {/* Feedback */}
      {!sunday && menu && (
        <div className="mt-2 pt-3 border-t">
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Feedback
          </label>
          <FeedbackInput
            value={feedbackText}
            onChange={(text) => onFeedbackChange(menu.id, text)}
            disabled={isFeedbackDisabled}
          />
          <MenuRatingStars summary={ratingSummary} disabled={isFeedbackDisabled || ratingPending} onRate={(rating) => onRatingChange(menu.id, rating)} />
        </div>
      )}
    </Card>
  );
}
